declare const FeBlazorLibrary: {
    codeSnippet: {
        unescapeHtml: (html: string) => string;
        removeTrash: (html: string) => string;
        beautifyCode: (html: string) => string;
        escapeHtml: (html: string) => string;
    };
};

declare const hljs: {
    highlightElement: (element: HTMLElement) => void;
    lineNumbersBlock: (element: HTMLElement) => void;
};

export default class CodeSnippet {
    /**
     * Pulls the code snippet content from the element
     * @param {any} el The code coding element
     * @returns The raw html/code
     */
    static fetchSnippetContents(el: HTMLElement): string {
        return FeBlazorLibrary.codeSnippet.unescapeHtml(el.innerHTML);
    }

    /**
     * This will highlight the contents of a code snippet element
     * @param {any} el The code containing element
     * @param {any} raw The raw code
     * @param {any} lang The language the code should be assumed
     * @param {any} shouldIncludeLineNumbers If line numbers should be shown
     * @returns A promise for the task
     */
    static highlightSnippetElements(el: HTMLElement, raw: HTMLElement, lang: string, shouldIncludeLineNumbers: boolean): Promise<void> {
        return new Promise((resolve, reject) => {
            try {
                let html = raw.innerHTML;

                html = FeBlazorLibrary.codeSnippet.removeTrash(html);
                html = FeBlazorLibrary.codeSnippet.beautifyCode(html);
                html = FeBlazorLibrary.codeSnippet.escapeHtml(html);
                el.classList.add('language-' + lang);
                raw.classList.add('language-' + lang);
                el.innerHTML = html;
                raw.innerHTML = html;
                hljs.highlightElement(el);

                if (shouldIncludeLineNumbers)
                    hljs.lineNumbersBlock(el);

                resolve();
            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * This will highlight the code snippet
     * @param {any} srcCode The raw code
     * @param {any} lang The language the code should be assumed
     * @returns A promise for the task
     */
    static highlightSnippet(srcCode: string, lang: string): Promise<string> {
        return new Promise((resolve, reject) => {
            try {
                let html = srcCode;

                // can we prehighlight the example code via compile or something and only do non-examples file here?
                html = FeBlazorLibrary.codeSnippet.removeTrash(html);
                html = FeBlazorLibrary.codeSnippet.beautifyCode(html);
                html = FeBlazorLibrary.codeSnippet.escapeHtml(html);

                const el = document.createElement('div');
                el.innerHTML = html;
                el.classList.add('language-' + lang);

                hljs.highlightElement(el);

                resolve(el.innerHTML);
            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * Adds line numbers to the element
     * @param {any} el The element to add line numbers to
     * @returns A promise to complete the task
     */
    static highlightAddLineNumbers(el: HTMLElement): Promise<void> {
        return new Promise((resolve, reject) => {
            try {
                hljs.lineNumbersBlock(el);

                resolve();
            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * Takes the element object and the content and sets the content
     * to that element
     * @param {any} element The element to assign contents to
     * @param {any} html The content
     */
    static setElementHtml(element: HTMLElement, html: string): void {
        element.innerHTML = html;
    }

    /**
     * Converts code to be escaped
     * @param {any} unsafe The unescaped code
     * @returns The escaped code
     */
    static escapeHtml(unsafe: string): string {
        return unsafe
            .replace(/&lt;(?!(script|\/script|link|\/link|html|\/html|body|\/body|!doctype))/g, "&amp;")           
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/&amp;lesst;/g, "&lt;")
            .replace(/'/g, "&#039;");
    }

    /**
     * Converts an escaped set of code to unescaped
     * @param {any} safe The escaped code
     * @returns The unescaped code
     */
    static unescapeHtml(safe: string): string {
        return safe
            .replace(/&amp;/g, "&")
            .replace(/&lt;/g, "<")
            .replace(/&lesst;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&quot;/g, "\"")
            .replace(/&#039;/g, "'");
    }

    /**
     * Removes stuff from the code that we do not care about
     * @param {any} s The code
     * @returns The code with less trash
     */
    static removeTrash(s: string): string {
        return s.replace('<!--!-->', ''); // Blazor injected comments
    }

    /**
     * Attempts to make the code a little cleaner by standardizing some
     * tabbing and such
     * @param {any} code The Code
     * @returns The more beautiful code
     */
    static beautifyCode(code: string): string {
        // Remove leading/trailing empty lines
        code = code.replace(/^\s*\n|\n\s*$/g, '');

        // Split the code into lines
        const lines = code.split('\n');

        // Find the minimum indentation
        const minIndent = Math.min(...lines.filter(line => line.trim() !== '').map(line => line.search(/\S/)));

        // Dedent the code
        const dedentedLines = lines.map(line => line.slice(minIndent));

        // Replace tabs with two spaces
        return dedentedLines.join('\n').replace(/\t/g, '  ');
    }
}
